import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

// Helper function to calculate pairs from records using FIFO
const calculatePairsFromRecords = (records) => {
  const entriesBySymbol = {};
  const exitsBySymbol = {};

  // Sort records by time
  const sortedRecords = [...records].sort((a, b) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  sortedRecords.forEach(record => {
    const symbol = record.symbol;
    if (record.action === 'entry') {
      if (!entriesBySymbol[symbol]) entriesBySymbol[symbol] = [];
      entriesBySymbol[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp,
        aiRecommended: record.aiRecommended
      });
    } else {
      if (!exitsBySymbol[symbol]) exitsBySymbol[symbol] = [];
      exitsBySymbol[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp
      });
    }
  });

  const pairs = [];
  Object.keys(entriesBySymbol).forEach(symbol => {
    const entries = [...entriesBySymbol[symbol]];
    const exits = exitsBySymbol[symbol] || [];

    exits.forEach(exit => {
      if (entries.length > 0) {
        const entry = entries.shift();
        const qty = Math.min(entry.quantity, exit.quantity);
        pairs.push({
          symbol,
          profit: (exit.price - entry.price) * qty,
          quantity: qty,
          entryPrice: entry.price,
          exitPrice: exit.price,
          exitTimestamp: exit.timestamp,
          entryTimestamp: entry.timestamp,
          entry: { uiRecord: { aiRecommended: entry.aiRecommended } }
        });
      }
    });
  });

  return pairs;
};

/**
 * Cumulative P&L Chart
 * Shows running total of profit/loss over time
 * Green area = profit zone, Red area = loss zone
 */
const CumulativePLChart = ({ records, pairs = [] }) => {
  // Calculate cumulative P&L from pairs (with fallback to records)
  const chartData = useMemo(() => {
    // Use provided pairs or calculate from records
    let pairsToUse = pairs && pairs.length > 0 ? pairs : calculatePairsFromRecords(records);

    if (pairsToUse.length === 0) {
      return [];
    }

    // Sort pairs by exit time
    const sortedPairs = [...pairsToUse]
      .filter(p => p.exitTimestamp || p.exit?.uiRecord?.timestamp)
      .sort((a, b) => {
        const timeA = new Date(a.exitTimestamp || a.exit?.uiRecord?.timestamp).getTime();
        const timeB = new Date(b.exitTimestamp || b.exit?.uiRecord?.timestamp).getTime();
        return timeA - timeB;
      });

    if (sortedPairs.length === 0) return [];

    // Calculate cumulative P&L
    let cumulative = 0;
    const data = sortedPairs.map((pair, index) => {
      const exitTime = new Date(pair.exitTimestamp || pair.exit?.uiRecord?.timestamp);
      const pnl = pair.profit || 0;
      cumulative += pnl;

      return {
        index,
        timestamp: exitTime.getTime(),
        date: exitTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: exitTime.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
        symbol: pair.symbol,
        pnl: pnl,
        cumulative: cumulative
      };
    });

    return data;
  }, [records, pairs]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isProfit = data.cumulative >= 0;
      const isPnlProfit = data.pnl >= 0;

      return (
        <div style={{
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          minWidth: '160px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
            {data.fullDate}
          </div>
          <div style={{ fontSize: '13px', marginBottom: '6px' }}>
            <span style={{ color: '#666' }}>Trade: </span>
            <span style={{ fontWeight: '600' }}>{data.symbol}</span>
          </div>
          <div style={{ fontSize: '13px', marginBottom: '6px' }}>
            <span style={{ color: '#666' }}>P&L: </span>
            <span style={{
              color: isPnlProfit ? '#22c55e' : '#ef4444',
              fontWeight: '600'
            }}>
              {isPnlProfit ? '+' : ''}${data.pnl.toFixed(2)}
            </span>
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: 'bold',
            paddingTop: '6px',
            borderTop: '1px solid #eee',
            color: isProfit ? '#22c55e' : '#ef4444'
          }}>
            Cumulative: {isProfit ? '+' : ''}${data.cumulative.toFixed(2)}
          </div>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div style={{
        height: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        No completed trades to display. Cumulative P&L requires matched Entry/Exit pairs.
      </div>
    );
  }

  // Calculate domain for Y axis
  const allValues = chartData.map(d => d.cumulative);
  const minValue = Math.min(0, ...allValues);
  const maxValue = Math.max(0, ...allValues);
  const padding = Math.max(Math.abs(maxValue), Math.abs(minValue)) * 0.1 || 100;

  // Final cumulative value
  const finalValue = chartData[chartData.length - 1].cumulative;
  const isOverallProfit = finalValue >= 0;

  return (
    <div style={{ width: '100%', height: 450 }}>
      {/* Summary */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '12px 16px',
        background: isOverallProfit ? '#f0fdf4' : '#fef2f2',
        borderRadius: '8px',
        border: `1px solid ${isOverallProfit ? '#bbf7d0' : '#fecaca'}`
      }}>
        <div>
          <span style={{ color: '#666', fontSize: '13px' }}>Total Trades: </span>
          <span style={{ fontWeight: '600' }}>{chartData.length}</span>
        </div>
        <div>
          <span style={{ color: '#666', fontSize: '13px' }}>Cumulative P&L: </span>
          <span style={{
            fontWeight: 'bold',
            fontSize: '18px',
            color: isOverallProfit ? '#22c55e' : '#ef4444'
          }}>
            {isOverallProfit ? '+' : ''}${finalValue.toFixed(2)}
          </span>
        </div>
      </div>

      <ResponsiveContainer>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, bottom: 20, left: 60 }}
        >
          <defs>
            <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05}/>
            </linearGradient>
            <linearGradient id="colorLoss" x1="0" y1="1" x2="0" y2="0">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            stroke="#666"
            fontSize={11}
            tickMargin={8}
          />
          <YAxis
            domain={[minValue - padding, maxValue + padding]}
            tickFormatter={(value) => `$${value.toFixed(0)}`}
            stroke="#666"
            fontSize={12}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="#333" strokeWidth={1} strokeDasharray="3 3" />

          {/* Area for profit zone (above 0) */}
          <Area
            type="monotone"
            dataKey="cumulative"
            stroke="#22c55e"
            strokeWidth={2}
            fill="url(#colorProfit)"
            baseValue={0}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CumulativePLChart;
